import React from "react";
import { useFieldArray } from "react-hook-form";
import { Button, Spinner, Collapse } from "reactstrap";
import CommonForm, { FormFieldConfig } from "Components/Common/CommonForm";
import SectionCard from "Components/Common/SectionCard";
import formStyles from "../../../../assets/scss/common/formStyle.module.scss";
import AdverseEventStepper from "./AdverseEventStepper";
import { Routeof_Administration_OPTIONS } from "api/modules/common.option";
import { validations } from "validations/FormValidations";

interface AdverseEventMedicationsFormProps {
  control: any;
  setValue: any;
  getValues: any;
  watch?: any;
  errors: any;
  clearErrors?: any;
  onSubmit: (data: any) => void;
  onBack: () => void;
  onNext: () => void;
  onCancel?: () => void;
  isPending?: boolean;
  isEditMode?: boolean;
  isSaved?: boolean;
  onDeleteMedicine?: (index: number, medicineId: number | null) => void;
  fields?: any[];
  append?: (value: any) => void;
  remove?: (index: number) => void;
}

const AdverseEventMedicationsForm: React.FC<AdverseEventMedicationsFormProps> = ({
  control,
  setValue,
  getValues,
  errors,
  onSubmit,
  onBack,
  onNext,
  isPending = false,
  isEditMode = false,
  isSaved = false,
  onDeleteMedicine,
  fields: externalFields,
  append: externalAppend,
  remove: externalRemove,
}) => {
  // Map errors into flat keys matching field names dynamically on every render
  const flatErrors: Record<string, any> = {};

  if (errors?.medicines) {
    Object.keys(errors.medicines).forEach((indexKey) => {
      const index = Number(indexKey);
      const med = (errors.medicines as any)[indexKey];
      if (med && typeof med === "object") {
        Object.keys(med).forEach((key) => {
          flatErrors[`medicines.${index}.${key}`] = med[key];
        });
      }
    });
  }

  Object.keys(errors || {}).forEach((key) => {
    if (key !== "medicines" && key !== "followups") {
      flatErrors[key] = errors[key];
    }
  });

  // Use field array to manage multiple medicines
  const { fields: internalFields, append: internalAppend, remove: internalRemove } = useFieldArray({
    control,
    name: "medicines",
  });

  const fields = externalFields || internalFields;
  const append = externalAppend || internalAppend;
  const remove = externalRemove || internalRemove;

  const [collapsedMap, setCollapsedMap] = React.useState<Record<string, boolean>>({});

  const resolutionFields: FormFieldConfig[] = [
    {
      name: "resolutiondate",
      required: true,
      label: "Resolution Date",
      type: "date",
      optionsProps: {
        maxDate: new Date(),
      },
    },
    {
      name: "resolutiontime",
      required: true,
      label: "Resolution Time",
      type: "time",
    },
    {
      name: "consumedmedication",
      label: "Has participant consumed all prescribed medication?",
      type: "select",
      required: true,
      options: [
        { label: "Yes", value: "yes" },
        { label: "No", value: "no" },
        { label: "NA", value: "na" },
      ],
      placeholder: "Select...",
    },
  ];

  const getMedicineFields = (index: number): FormFieldConfig[] => [
    {
      name: `medicines.${index}.brandname`,
      label: "Brand Name",
      type: "text",
      required: true,
      placeholder: "Brand name...",
      rules: validations.adverseEvent.brandname,
    },
    {
      name: `medicines.${index}.genericname`,
      label: "Generic Name with Strength",
      type: "text",
      required: true,
      placeholder: "e.g. Paracetamol 500mg...",
      rules: validations.adverseEvent.genericname,
    },
    {
      name: `medicines.${index}.frequency`,
      label: "Frequency",
      type: "text",
      required: true,
      placeholder: "e.g. TDS, BD, OD...",
      rules: validations.adverseEvent.frequency,
    },
    {
      name: `medicines.${index}.route`,
      label: "Route of Administration",
      type: "select",
      required: true,
      options: Routeof_Administration_OPTIONS,
      placeholder: "Select Route...",
    },
    {
      name: `medicines.${index}.startdate`,
      label: "Start Date",
      type: "date",
      required: true,
      optionsProps: {
        maxDate: new Date(),
      },
    },
    {
      name: `medicines.${index}.enddate`,
      label: "End Date",
      type: "date",
      optionsProps: {
        maxDate: new Date(),
      },
      rules: {
        validate: (value: string) => {
          if (!value) return true;
          const start = getValues(`medicines.${index}.startdate`);
          if (!start) return true;
          if (new Date(value) < new Date(start)) {
            return "End date cannot be before start date";
          }
          return true;
        },
      },
    },
    {
      name: `medicines.${index}.interaction`,
      label: "Interaction with I.P.",
      type: "text",
      placeholder: "Enter details...",
      rules: validations.adverseEvent.interaction,
    },
  ];

  return (
    <form className={formStyles.formShell} onSubmit={onSubmit}>
      {!isEditMode && (
        <>
          {/* Page Header */}
          <div className="mb-4">
            <h4 className="mb-0 fw-700">Log New Adverse Event</h4>
          </div>

          <AdverseEventStepper activeTab="medications" />
        </>
      )}

      {/* Medicines Section Header and Add Button */}
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h5 className="mb-0 fw-semibold text-muted">Medicines used to treat adverse event</h5>
        {!isSaved && (
          <Button
            type="button"
            color="primary"
            outline
            size="sm"
            onClick={() => {
              // Collapse all existing medicines
              const newCollapsedMap = { ...collapsedMap };
              fields.forEach((field) => {
                newCollapsedMap[field.id] = true;
              });
              setCollapsedMap(newCollapsedMap);

              append({
                brandname: "",
                genericname: "",
                frequency: "",
                route: "",
                startdate: "",
                enddate: "",
                interaction: "",
                recordedby: "",
              });
            }}
          >
            <i className="ri-add-line me-1" /> Add Medicine
          </Button>
        )}
      </div>

      {/* List of Card-wise Medicine forms */}
      {fields.length === 0 ? (
        <div className="card border shadow-sm p-4 text-center text-muted mb-4" style={{ borderRadius: "12px" }}>
          <p className="mb-0">No medicines added yet. Click "Add Medicine" to record concomitant medications.</p>
        </div>
      ) : (
        fields.map((field, index) => {
          const hasErrors = !!errors?.medicines?.[index];
          const isCollapsed = hasErrors ? false : !!collapsedMap[field.id];

          return (
            <div className={formStyles.sectionCard} key={field.id}>
              <div
                className={formStyles.sectionHeader}
                style={{ cursor: "pointer", userSelect: "none" }}
                onClick={() => {
                  setCollapsedMap((prev) => ({
                    ...prev,
                    [field.id]: !prev[field.id],
                  }));
                }}
              >
                <i className={`ri-capsule-line ${formStyles.sectionIcon}`} />
                <div className={formStyles.sectionTitleWrap}>
                  <p className={formStyles.sectionTitle}>
                    {`Medicine ${String(index + 1).padStart(2, "0")}`}
                  </p>
                </div>
                <div
                  className="d-flex align-items-center gap-2 ms-auto"
                  onClick={(e) => e.stopPropagation()}
                >
                  {!isSaved && (
                    <i
                      className="ri-delete-bin-line"
                      title="Delete Medicine"
                      style={{ fontSize: 16, color: "#dc3545", cursor: "pointer" }}
                      onClick={(e) => {
                        e.stopPropagation();
                        const med = fields[index] as any;
                        const medId = med?.medicineid ? Number(med.medicineid) : null;
                        if (isEditMode && onDeleteMedicine) {
                          onDeleteMedicine(index, medId);
                        } else {
                          remove(index);
                        }
                      }}
                    />
                  )}
                  <i
                    className={isCollapsed ? "ri-arrow-down-s-line" : "ri-arrow-up-s-line"}
                    style={{ fontSize: "20px", color: "#8a96a3", cursor: "pointer" }}
                    onClick={() => {
                      setCollapsedMap((prev) => ({
                        ...prev,
                        [field.id]: !prev[field.id],
                      }));
                    }}
                  />
                </div>
              </div>
              <Collapse isOpen={!isCollapsed}>
                <div className={formStyles.sectionBody}>
                  <div
                    className="p-3 mb-2"
                    style={{
                      backgroundColor: "#fff",
                      position: "relative",
                    }}
                  >
                    <CommonForm
                      fields={getMedicineFields(index)}
                      control={control}
                      errors={flatErrors}
                      columns={3}
                      setValue={setValue}
                    />
                  </div>
                </div>
              </Collapse>
            </div>
          );
        })
      )}

      {/* Resolution Details Section */}
      <div className="mt-4">
        <SectionCard
          index={1}
          icon="ri-time-line"
          title="Resolution Details"
          subtitle="Capture resolution timing, date, recorder details and medication consumption."
        >
          <CommonForm
            fields={resolutionFields}
            control={control}
            errors={flatErrors}
            columns={2}
            setValue={setValue}
          />
        </SectionCard>
      </div>

      {/* Sticky Footer */}
      <div className={formStyles.stickyFooter}>
        <div className={formStyles.footerActions}>
          <div className="d-flex gap-2">
            <Button
              type="button"
              color="primary"
              outline
              onClick={onBack}
            >
              Back
            </Button>
          </div>
          <div className="d-flex gap-2">
            {!isSaved && (
              <Button
                type="submit"
                color="primary"
                outline
                disabled={isPending}
              >
                {isPending ? (
                  <>
                    <Spinner size="sm" className="me-2" />
                    {isEditMode ? "Updating..." : "Saving..."}
                  </>
                ) : (
                  <>{isEditMode ? "Update" : "Save"}</>
                )}
              </Button>
            )}
            <Button
              type="button"
              color="primary"
              outline
              onClick={onNext}
              disabled={!isSaved && !isEditMode}
            >
              Next
            </Button>
          </div>
        </div>
      </div>
    </form>
  );
};

export default AdverseEventMedicationsForm;
